import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTExpander, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TextOverflowTooltip } from 'src/common';
import { ControlFrameworkDetailsQuery } from 'src/graphql/_generated';
import { GTInternalIds } from 'src/components/GenericTable/types';

type FrameworkLibraryItem = ControlFrameworkDetailsQuery['controlFrameworks'][number];

type UseColumns = (a: {
  data?: FrameworkLibraryItem[];
  tableId: TableIds;
  onFrameworkSelect: (a: FrameworkLibraryItem) => void;
}) => {
  columns: ColumnDef<FrameworkLibraryItem>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['framework', 'author', 'name'];

export const useFrameworkColumns: UseColumns = ({ data = [], tableId, onFrameworkSelect }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${tableId}-columns`, defaultColumns);

  const columns = useMemo(() => {
    let internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

    const list: ColumnDef<FrameworkLibraryItem>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },

      {
        id: 'framework',
        header: 'Framework',
        accessorFn: (row) => row.shortname,
        cell: ({ row }) => (
          <span
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => {
              onFrameworkSelect(row.original);
            }}
          >
            {row.original.shortname}
          </span>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'author',
        header: 'Author',
        accessorFn: (row) => row.author,
        cell: ({ row }) => row.original.author,
        sortingFn: sortingFns.text,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: ({ row }) => <TextOverflowTooltip>{row.original.name}</TextOverflowTooltip>,
        sortingFn: sortingFns.text,
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          return (
            <div css="display: flex;grid-gap: 8px;">
              <GTTdm />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={tableId}
      options={[
        { key: 'framework', title: 'Framework' },
        { key: 'author', title: 'Author' },
        { key: 'name', title: 'Name' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
